import css from '@/components/ui/shared/Table/Table.module.css';
import { useTableContext } from '@/components/ui/shared/Table/TableContext';
import { TypedCheckbox as Checkbox } from '@/components/ui/shared/Table/internal/TypedCheckbox';
import { classNames } from '@/utils/helpers/classNames';
import { useId } from 'react';
import type { ReactNode } from 'react';

const LABELS = { selectAll: 'выбрать все' };

interface Props {
  children: ReactNode;
}

export function TableHead({ children }: Props) {
  const { selectable, expandable, rowIds, selectedIds, onSelectionChange } =
    useTableContext();
  const baseId = useId();

  const allSelected =
    rowIds.length > 0 && rowIds.every(id => selectedIds.has(id));
  const someSelected = rowIds.some(id => selectedIds.has(id));

  const toggleAll = () => {
    if (!onSelectionChange) return;
    const next = new Set(selectedIds);
    rowIds.forEach(id => {
      if (allSelected) {
        next.delete(id);
      } else {
        next.add(id);
      }
    });
    onSelectionChange(next);
  };

  // Without a checkbox/chevron column, the row has nothing reserving its
  // left inset (see Row.tsx), so add it back directly on the row.
  const hasLeadingColumn = selectable || expandable;

  return (
    <div role="rowgroup">
      <div
        role="row"
        className={classNames({
          [css.headerRow]: true,
          [css.rowNoLeadingColumn]: !hasLeadingColumn
        })}
      >
        {selectable && (
          <div role="columnheader" className={css.checkboxCol}>
            <Checkbox
              id={`${baseId}-select-all`}
              checked={allSelected}
              indeterminate={someSelected && !allSelected}
              onChange={toggleAll}
              invertedColor
              ariaLabel={LABELS.selectAll}
            />
          </div>
        )}
        {expandable && <div role="columnheader" className={css.expandCol} />}
        {children}
      </div>
    </div>
  );
}
